import React, { useState } from 'react';
import { ModalProps } from 'types/Modal';
import { DataType, ShareRequestItem } from 'types/Share';
import LastStep from 'components/vertical-stepper/LastStep';
import { NDialog, NLink } from '@innersource-nn/novoui-react';
import { useNavigate } from 'react-router-dom';
import SelectEnvironmentStep from 'views/Datasets/components/RequestAccess/SelectEnvironmentStep';

export interface TableAccessModalProps extends ModalProps {
    resourceUri: string;
    label: string;
    datasetLabel: string;
}

export default function TableAccessModal({
    visible,
    onClose,
    resourceUri,
    label,
    datasetLabel,
}: TableAccessModalProps) {
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState<number>(0);
    const [newTable, setNewTable] = useState<ShareRequestItem>({
        datasetUri: '',
        name: '',
        shareUri: '',
        tableUri: '',
    });
    const handleShareRequest = (shareRequestItem: ShareRequestItem) => {
        setNewTable({ ...shareRequestItem });
        setActiveStep(1);
    };
    const handleClickSuccess = () => {
        navigate(`/share/${newTable.shareUri}`);
        if (onClose) onClose();
    };

    return (
        visible &&
        onClose && (
            <NDialog
                isVisible={visible}
                closeDialog={onClose}
                backgroundClose
                headline={`Request access for ${label}`}
                size="medium"
                showDividers={false}
            >
                {activeStep === 1 ? (
                    !newTable.tableUri ? (
                        newTable.shareUri === '' ? (
                            <LastStep
                                title="Request failed"
                                type="error"
                                description={
                                    <p>
                                        We encountered an issue while processing
                                        your request for access to &nbsp;
                                        <NLink
                                            href={`/table/${resourceUri}`}
                                            className="underline"
                                        >
                                            {label}
                                        </NLink>
                                        &nbsp; within &nbsp;
                                        <NLink
                                            href={`/dataset/${newTable.datasetUri}`}
                                            className="underline"
                                        >
                                            {datasetLabel}
                                        </NLink>
                                        . Please try again.
                                    </p>
                                }
                                handleCancel={onClose}
                                primaryButtonAction={() => setActiveStep(0)}
                                primaryButtonText="Retry request"
                                secondaryButtonText="Close"
                            />
                        ) : (
                            <LastStep
                                title="Request already exist"
                                type="info"
                                description={
                                    <p>
                                        You already have an existing request
                                        for&nbsp;
                                        <NLink
                                            href={`/table/${resourceUri}`}
                                            className="underline"
                                            onClick={() => onClose()}
                                        >
                                            {label}
                                        </NLink>
                                        &nbsp; within &nbsp;
                                        <NLink
                                            href={`/dataset/${newTable.datasetUri}`}
                                            className="underline"
                                        >
                                            {datasetLabel}
                                        </NLink>
                                        &nbsp; for the targeted environment and
                                        target group.
                                    </p>
                                }
                                handleCancel={onClose}
                                primaryButtonAction={handleClickSuccess}
                                primaryButtonText="View existing request"
                                secondaryButtonText="Go back"
                                secondaryButtonAction={() => setActiveStep(0)}
                            />
                        )
                    ) : (
                        <LastStep
                            title="Request sent!"
                            type="success"
                            description={
                                <p>
                                    Your request for&nbsp;
                                    <NLink
                                        href={`/table/${resourceUri}`}
                                        className="underline"
                                        onClick={() => onClose()}
                                    >
                                        {label}
                                    </NLink>
                                    &nbsp; within &nbsp;
                                    <NLink
                                        href={`/dataset/${newTable.datasetUri}`}
                                        className="underline"
                                    >
                                        {datasetLabel}
                                    </NLink>
                                    &nbsp; has been sent, you will be notified
                                    here on datahub and through email as soon as
                                    your request has been processed by one of
                                    the owners/stewards.
                                </p>
                            }
                            handleCancel={onClose}
                            primaryButtonAction={handleClickSuccess}
                            primaryButtonText="See share request"
                            secondaryButtonText="Close"
                        />
                    )
                ) : (
                    <SelectEnvironmentStep
                        dataType={DataType['table']}
                        resourceUri={resourceUri}
                        handleShareRequest={handleShareRequest}
                        buttonText={'Request access'}
                    />
                )}
            </NDialog>
        )
    );
}
